/**
 * Dico localisé de la page « À propos » (FR `/a-propos` · EN `/en/about` ·
 * AR `/ar/a-propos`).
 *
 * Regroupe TOUT le texte rendu par la page : le fil d'Ariane et l'ensemble des
 * chaînes de `CredibilityExperience`. Le FR reste BYTE-IDENTIQUE à l'existant
 * (zéro régression) ; l'EN et l'AR (arabe standard moderne / فصحى, registre
 * premium) sont des traductions complètes. Lu via `tr(ABOUT_UI, locale)` qui
 * retombe sur le FR si une locale manque.
 *
 * Les hrefs, icônes, nombres et images vivent dans les composants : ce dico ne
 * porte que du texte.
 */
import type { I18n } from "@/config/localeText";

type Card = { title: string; text: string };
type Step = { step: string; title: string; text: string };

export type AboutUi = {
  /** Fil d'Ariane. */
  breadcrumb: { home: string; current: string };
  /** Hero de crédibilité. */
  hero: {
    eyebrow: string;
    title: string;
    intro: string;
    trustSignals: string[];
    imageAlt: string;
    reputationEyebrow: string;
    reputationText: string;
  };
  /** Section « Preuves terrain ». */
  proof: {
    eyebrow: string;
    heading: string;
    moments: Card[];
  };
  /** Section « Méthode d'exécution ». */
  process: {
    eyebrow: string;
    heading: string;
    steps: Step[];
    stepLabel: string;
    aside: {
      eyebrow: string;
      title: string;
      text: string;
    };
  };
  /** Section « Standards qualité ». */
  quality: {
    eyebrow: string;
    heading: string;
    commitments: Card[];
  };
};

export const ABOUT_UI: I18n<AboutUi> = {
  fr: {
    breadcrumb: { home: "Accueil", current: "À propos" },
    hero: {
      eyebrow: "Crédibilité KS Rent Services",
      title: "Une marque construite sur des preuves, pas sur des promesses",
      intro:
        "Depuis Cannes, KS Rent Services opère des missions chauffeur et location premium avec un suivi humain unique, des standards constants et une exécution lisible pour chaque client.",
      trustSignals: [
        "Fondée en 2022 par Sofiane Khoula, président",
        "Double expertise: chauffeur + location premium",
        "Références clients en environnement exigeant",
        "Exécution cadrée de la demande à la mission",
      ],
      imageAlt: "Preuve sociale et crédibilité KS Rent Services",
      reputationEyebrow: "Focus réputation",
      reputationText:
        "Des références visibles et des retours clients positifs qui confirment notre capacité 'exécution dans des contextes premium.",
    },
    proof: {
      eyebrow: "Preuves terrain",
      heading: "Contextes réels où la qualité doit être constante",
      moments: [
        {
          title: "Coordination événementielle",
          text: "Gestion des horaires sensibles, des flux invités et des points de prise en charge multiples.",
        },
        {
          title: "Expérience passager premium",
          text: "Discrétion, confort et posture professionnelle sur chaque déplacement.",
        },
        {
          title: "Flotte et formats adaptés",
          text: "Choix du véhicule aligné au contexte: business, événement, usage privé ou image de marque.",
        },
      ],
    },
    process: {
      eyebrow: "Méthode 'exécution",
      heading: "Un process lisible, piloté de bout en bout",
      steps: [
        {
          step: "01",
          title: "Qualification précise",
          text: "Contexte, timing, contraintes et niveau de confidentialité sont cadrés dès le départ.",
        },
        {
          step: "02",
          title: "Choix du bon dispositif",
          text: "Nous orientons vers le format adapté: chauffeur dédié, location premium ou orchestration hybride.",
        },
        {
          step: "03",
          title: "Exécution pilotée",
          text: "Suivi opérationnel continu jusqu'à la finalisation de la mission, avec communication claire.",
        },
      ],
      stepLabel: "Étape",
      aside: {
        eyebrow: "Service chauffeur premium",
        title: "Notre méthode de service sur la Côte d'Azur",
        text: "Chez KS Rent Services, chaque mission est cadrée avec un interlocuteur unique, un suivi horaire clair et une exécution terrain fiable de Cannes à Monaco, Nice et Sophia.",
      },
    },
    quality: {
      eyebrow: "Standards qualité",
      heading: "Les engagements qui sécurisent l'expérience client",
      commitments: [
        {
          title: "Ponctualité maîtrisée",
          text: "Marges de sécurité, préparation des itinéraires et pilotage en temps réel.",
        },
        {
          title: "Discrétion opérationnelle",
          text: "Posture, confidentialité et adaptation aux environnements VIP et corporate.",
        },
        {
          title: "Lisibilité du service",
          text: "Interlocuteur clair, informations utiles, jalons compréhensibles à chaque étape.",
        },
        {
          title: "Constance de qualité",
          text: "Même niveau d'exigence sur chaque demande, du premier contact au retour client.",
        },
      ],
    },
  },

  en: {
    breadcrumb: { home: "Home", current: "About" },
    hero: {
      eyebrow: "KS Rent Services credibility",
      title: "A brand built on proof, not on promises",
      intro:
        "From Cannes, KS Rent Services runs chauffeur missions and premium car rental with a single human point of contact, consistent standards and clear execution for every client.",
      trustSignals: [
        "Founded in 2022 by Sofiane Khoula, President",
        "Dual expertise: chauffeur + premium rental",
        "Client references in demanding environments",
        "Structured execution from request to mission",
      ],
      imageAlt: "Social proof and credibility of KS Rent Services",
      reputationEyebrow: "Reputation focus",
      reputationText:
        "Visible references and positive client feedback that confirm our ability to deliver in premium contexts.",
    },
    proof: {
      eyebrow: "Field proof",
      heading: "Real-world contexts where quality must stay constant",
      moments: [
        {
          title: "Event coordination",
          text: "Managing sensitive schedules, guest flows and multiple pick-up points.",
        },
        {
          title: "Premium passenger experience",
          text: "Discretion, comfort and a professional manner on every journey.",
        },
        {
          title: "Fleet and formats to match",
          text: "Vehicle chosen to fit the context: business, event, private use or brand image.",
        },
      ],
    },
    process: {
      eyebrow: "Execution method",
      heading: "A clear process, driven end to end",
      steps: [
        {
          step: "01",
          title: "Precise qualification",
          text: "Context, timing, constraints and level of confidentiality are framed from the start.",
        },
        {
          step: "02",
          title: "Choosing the right setup",
          text: "We steer you toward the right format: dedicated chauffeur, premium rental or a hybrid arrangement.",
        },
        {
          step: "03",
          title: "Managed execution",
          text: "Continuous operational follow-up through to mission completion, with clear communication.",
        },
      ],
      stepLabel: "Step",
      aside: {
        eyebrow: "Premium chauffeur service",
        title: "Our service method on the French Riviera",
        text: "At KS Rent Services, every mission is framed with a single point of contact, clear time tracking and reliable on-the-ground execution from Cannes to Monaco, Nice and Sophia.",
      },
    },
    quality: {
      eyebrow: "Quality standards",
      heading: "The commitments that secure the client experience",
      commitments: [
        {
          title: "Mastered punctuality",
          text: "Safety margins, route preparation and real-time management.",
        },
        {
          title: "Operational discretion",
          text: "Bearing, confidentiality and adaptation to VIP and corporate environments.",
        },
        {
          title: "Service clarity",
          text: "A clear point of contact, useful information and understandable milestones at every step.",
        },
        {
          title: "Consistent quality",
          text: "The same level of demand on every request, from first contact to client follow-up.",
        },
      ],
    },
  },

  ar: {
    breadcrumb: { home: "الرئيسية", current: "من نحن" },
    hero: {
      eyebrow: "مصداقية KS Rent Services",
      title: "علامة تجارية مبنية على البراهين، لا على الوعود",
      intro:
        "انطلاقًا من كان، تتولى KS Rent Services مهام السائق الخاص وتأجير السيارات الفاخرة عبر مُحاوِر واحد ومتابعة إنسانية شخصية، بمعايير ثابتة وتنفيذ واضح لكل عميل.",
      trustSignals: [
        "تأسست عام 2022 على يد سفيان خولة، الرئيس",
        "خبرة مزدوجة: سائق خاص + تأجير فاخر",
        "مراجع عملاء في بيئات شديدة المتطلبات",
        "تنفيذ مُحكَم من الطلب حتى إتمام المهمة",
      ],
      imageAlt: "الدليل الاجتماعي ومصداقية KS Rent Services",
      reputationEyebrow: "تركيز على السمعة",
      reputationText:
        "مراجع ظاهرة وآراء إيجابية من العملاء تؤكد قدرتنا على التنفيذ في السياقات الفاخرة.",
    },
    proof: {
      eyebrow: "براهين ميدانية",
      heading: "سياقات واقعية يجب أن تبقى فيها الجودة ثابتة",
      moments: [
        {
          title: "تنسيق الفعاليات",
          text: "إدارة المواعيد الحساسة، وتدفق الضيوف، ونقاط الاستقبال المتعددة.",
        },
        {
          title: "تجربة ركاب فاخرة",
          text: "تحفّظ وراحة وحضور احترافي في كل تنقّل.",
        },
        {
          title: "أسطول وصيغ ملائمة",
          text: "اختيار المركبة بما يتوافق مع السياق: أعمال، فعالية، استخدام خاص أو صورة العلامة التجارية.",
        },
      ],
    },
    process: {
      eyebrow: "منهجية التنفيذ",
      heading: "مسار واضح، مُدار من البداية إلى النهاية",
      steps: [
        {
          step: "01",
          title: "تحديد دقيق للاحتياجات",
          text: "يُحدَّد السياق والتوقيت والقيود ومستوى السرّية منذ البداية.",
        },
        {
          step: "02",
          title: "اختيار الترتيب المناسب",
          text: "نوجّهك نحو الصيغة الملائمة: سائق مخصّص، أو تأجير فاخر، أو تنظيم هجين.",
        },
        {
          step: "03",
          title: "تنفيذ مُدار",
          text: "متابعة تشغيلية مستمرة حتى إتمام المهمة، مع تواصل واضح.",
        },
      ],
      stepLabel: "خطوة",
      aside: {
        eyebrow: "خدمة سائق خاص فاخرة",
        title: "منهجية خدمتنا على الريفييرا الفرنسية",
        text: "في KS Rent Services، تُؤطَّر كل مهمة بمُحاوِر واحد ومتابعة زمنية واضحة وتنفيذ ميداني موثوق من كان إلى موناكو ونيس وصوفيا.",
      },
    },
    quality: {
      eyebrow: "معايير الجودة",
      heading: "الالتزامات التي تضمن تجربة العميل",
      commitments: [
        {
          title: "انضباط في المواعيد",
          text: "هوامش أمان، وتحضير لخطوط السير، وإدارة في الوقت الفعلي.",
        },
        {
          title: "تحفّظ تشغيلي",
          text: "حضور لائق وسرّية وتكيّف مع بيئات كبار الشخصيات والشركات.",
        },
        {
          title: "وضوح الخدمة",
          text: "مُحاوِر واضح، ومعلومات مفيدة، ومراحل مفهومة في كل خطوة.",
        },
        {
          title: "ثبات الجودة",
          text: "المستوى نفسه من المتطلبات في كل طلب، من أول تواصل حتى متابعة العميل.",
        },
      ],
    },
  },
};
